import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  RemoveRedEye as RemoveRedEyeIcon,
  SettingsBackupRestore as RestoreIcon,
  DoDisturb as UnVerifiedIcon,
  CheckCircleOutline as VerifiedIcon,
  Search as SearchIcon,
  Groups as GroupsIcon,
  PersonOutline as PersonOutlineIcon,
  AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
} from "@mui/icons-material";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutline";

import useUser from "../../../../hooks/super/useUser";
import useAuth from "../../../../hooks/useAuth";

import UserModal from "./M_User";
import DeleteUserModal from "./M_DeleteUser";
import RestoreUserModal from "./M_RestoreUser";
import VerifyUserModal from "./M_VerifyUser";
import ViewUserModal from "./M_ViewUser";

import { formatDate, getUserRoleString } from "../../../../utils/format";
import { getComparator, stableSort } from "../../../../utils/tableSort";
import { UserType } from "../../../../utils/constants";

import {
  TOKENS,
  StatTile,
  StatusPill,
  Monogram,
  RowMenu,
  EmptyState,
} from "../../../../components/pages/_new/super/design";

const headCells = [
  { id: "firstName", label: "Administrator", sortable: true },
  { id: "role", label: "Role", sortable: true },
  { id: "title", label: "Title", sortable: true },
  { id: "isVerified", label: "Verified", sortable: true },
  { id: "isDeleted", label: "Status", sortable: true },
  { id: "created", label: "Created", sortable: true },
  { id: "action", label: "", sortable: false },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{ color: `${TOKENS.fg2} !important`, "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` } }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

const IdMono = ({ value }) => (
  <Typography
    sx={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 11,
      color: TOKENS.fg3,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {value}
  </Typography>
);

const VerifiedDot = ({ value }) =>
  value ? (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <VerifiedIcon sx={{ fontSize: 16, color: TOKENS.successInk }} />
      <Typography sx={{ fontSize: 13, color: TOKENS.successInk, fontWeight: 500 }}>Verified</Typography>
    </Stack>
  ) : (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <UnVerifiedIcon sx={{ fontSize: 16, color: TOKENS.fg3 }} />
      <Typography sx={{ fontSize: 13, color: TOKENS.fg3 }}>Unverified</Typography>
    </Stack>
  );

function AdminTable() {
  const { viewAdmin, admins } = useUser();
  const { adminUserType } = useAuth();

  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("created");
  const [search, setSearch] = React.useState("");

  // modal state
  const [open, setOpen] = React.useState(false);
  const handleClose = () => setOpen(false);
  const [viewOpen, setViewOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [restoreOpen, setRestoreOpen] = React.useState(false);
  const [verifyOpen, setVerifyOpen] = React.useState(false);
  const [removeFlag, setRemoveFlag] = React.useState(false);

  const [uid, setUid] = React.useState(0);
  const [email, setEmail] = React.useState();
  const [userName, setUserName] = React.useState([]);
  const [user, setUser] = React.useState();
  const [loading, setLoading] = React.useState(false);

  const handleSort = (id) => {
    if (id === orderBy) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const prepareAction = (row) => {
    setUserName([{ id: row.id, email: row.email }]);
  };

  const onView = async (row) => {
    setUid(row.id);
    setLoading(true);
    setViewOpen(true);
    const u = await viewAdmin(row.id);
    setUser(u);
    setLoading(false);
  };
  const onEdit = async (row) => {
    setUid(row.id);
    setLoading(true);
    setOpen(true);
    const u = await viewAdmin(row.id);
    setUser(u);
    setLoading(false);
  };
  const onVerify = (row) => {
    setUid(row.id);
    setEmail(row.email);
    prepareAction(row);
    setVerifyOpen(true);
  };
  const onDelete = (row) => {
    setUid(row.id);
    setRemoveFlag(false);
    prepareAction(row);
    setDeleteOpen(true);
  };
  const onRemove = (row) => {
    setUid(row.id);
    setRemoveFlag(true);
    prepareAction(row);
    setDeleteOpen(true);
  };
  const onRestore = (row) => {
    setUid(row.id);
    prepareAction(row);
    setRestoreOpen(true);
  };

  // counts for stat tiles
  const counts = React.useMemo(() => {
    const list = Array.isArray(admins) ? admins : [];
    let total = list.length;
    let active = 0;
    let unverified = 0;
    let suspended = 0;
    list.forEach((u) => {
      if (u?.isDeleted) suspended += 1;
      else active += 1;
      if (!u?.isVerified) unverified += 1;
    });
    return { total, active, unverified, suspended };
  }, [admins]);

  // search + sort
  const filtered = React.useMemo(() => {
    const list = Array.isArray(admins) ? admins : [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((row) => {
      const fullName = `${row?.firstName || ""} ${row?.lastName || ""}`.toLowerCase();
      return (
        (row?.email || "").toLowerCase().includes(q) ||
        fullName.includes(q) ||
        (row?.title || "").toLowerCase().includes(q) ||
        String(row?.id || "").toLowerCase().includes(q)
      );
    });
  }, [admins, search]);

  const sorted = React.useMemo(() => stableSort(filtered, getComparator(order, orderBy)), [filtered, order, orderBy]);

  const isSuper = UserType.SUPER_ADMIN === adminUserType;
  const isLoading = admins === null;

  return (
    <Box>
      {/* Summary tiles */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatTile
          icon={<GroupsIcon />}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.total}
          label="Total administrators"
        />
        <StatTile
          icon={<VerifiedIcon />}
          iconBg={TOKENS.successBg}
          iconInk={TOKENS.successInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.active}
          label="Active"
        />
        <StatTile
          icon={<UnVerifiedIcon />}
          iconBg={TOKENS.warningBg}
          iconInk={TOKENS.warningInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.unverified}
          label="Unverified"
        />
        <StatTile
          icon={<AdminPanelSettingsOutlinedIcon />}
          iconBg={TOKENS.dangerBg}
          iconInk={TOKENS.dangerInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.suspended}
          label="Suspended"
        />
      </Box>

      {/* Main panel */}
      <Box
        sx={{
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusM}px`,
          background: TOKENS.bgSurface,
          boxShadow: TOKENS.shadowXs,
          overflow: "hidden",
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            p: "14px 16px",
            borderBottom: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search by name, email, title, id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TOKENS.fg3 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 280,
              "& .MuiOutlinedInput-root": { borderRadius: "10px", background: TOKENS.bgSurface },
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3 }}>
            {isLoading ? "Loading..." : `${sorted.length} of ${counts.total}`}
          </Typography>
        </Box>

        <TableContainer>
          <Table aria-label="administrators table" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {headCells.map((c) => (
                  <HeaderCell
                    key={c.id}
                    id={c.id}
                    sortable={c.sortable}
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align={c.id === "action" ? "right" : "left"}
                  >
                    {c.label}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {headCells.map((__, j) => (
                        <DataCell key={`sk-${i}-${j}`}>
                          <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                        </DataCell>
                      ))}
                    </TableRow>
                  ))
                : sorted.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                        <EmptyState
                          icon={<PersonOutlineIcon />}
                          title={search ? "No administrators match your search" : "No administrators yet"}
                          body={search ? "Try a different keyword." : "Use the Add Administrator button to invite one."}
                        />
                      </TableCell>
                    </TableRow>
                  )
                : sorted.map((row) => {
                    const fullName = `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.email || "—";
                    const actions = [
                      {
                        key: "view",
                        icon: <RemoveRedEyeIcon fontSize="small" />,
                        label: "View",
                        onClick: () => onView(row),
                      },
                    ];
                    if (isSuper) {
                      actions.push({
                        key: "edit",
                        icon: <EditIcon fontSize="small" />,
                        label: "Edit",
                        onClick: () => onEdit(row),
                      });
                      if (!row?.isVerified) {
                        actions.push({
                          key: "verify",
                          icon: <VerifiedIcon fontSize="small" />,
                          label: "Verify",
                          onClick: () => onVerify(row),
                        });
                      }
                      if (row?.isDeleted) {
                        actions.push({
                          key: "restore",
                          icon: <RestoreIcon fontSize="small" />,
                          label: "Restore",
                          onClick: () => onRestore(row),
                        });
                      } else {
                        actions.push({
                          key: "delete",
                          icon: <DeleteIcon fontSize="small" />,
                          label: "Delete",
                          danger: true,
                          onClick: () => onDelete(row),
                        });
                      }
                      actions.push({ key: "div", divider: true });
                      actions.push({
                        key: "remove",
                        icon: <RemoveIcon fontSize="small" />,
                        label: "Remove permanently",
                        danger: true,
                        onClick: () => onRemove(row),
                      });
                    }
                    return (
                      <TableRow
                        key={row?.id}
                        hover
                        sx={{
                          "&:hover": { background: TOKENS.bgSunken },
                        }}
                      >
                        <DataCell>
                          <Stack direction="row" alignItems="center" spacing={1.25}>
                            <Monogram name={fullName} size={36} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 13.5,
                                  fontWeight: 600,
                                  color: TOKENS.fg1,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {fullName}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: TOKENS.fg2 }}>{row?.email || "—"}</Typography>
                            </Box>
                          </Stack>
                        </DataCell>
                        <DataCell>
                          <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>{getUserRoleString(row?.role) || "—"}</Typography>
                        </DataCell>
                        <DataCell>
                          <Typography sx={{ fontSize: 13, color: TOKENS.fg2 }}>{row?.title || "—"}</Typography>
                        </DataCell>
                        <DataCell>
                          <VerifiedDot value={row?.isVerified} />
                        </DataCell>
                        <DataCell>
                          <StatusPill status={row?.isDeleted ? "Suspended" : "Active"} />
                        </DataCell>
                        <DataCell>
                          <Stack spacing={0.25}>
                            <Typography sx={{ fontSize: 12.5, color: TOKENS.fg1 }}>
                              {row?.created ? formatDate(row.created) : "—"}
                            </Typography>
                            <IdMono value={row?.id} />
                          </Stack>
                        </DataCell>
                        <DataCell align="right">
                          <RowMenu actions={actions} />
                        </DataCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <UserModal open={open} handleClose={handleClose} user={user} loading={loading} adminFlag={true} />
      <ViewUserModal open={viewOpen} handleClose={() => setViewOpen(false)} user={user} loading={loading} />
      <DeleteUserModal
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        uid={uid}
        userName={userName}
        removeFlag={removeFlag}
        adminFlag={true}
      />
      <RestoreUserModal
        open={restoreOpen}
        handleClose={() => setRestoreOpen(false)}
        uid={uid}
        userName={userName}
        adminFlag={true}
      />
      <VerifyUserModal
        open={verifyOpen}
        handleClose={() => setVerifyOpen(false)}
        email={email}
        userName={userName}
        adminFlag={true}
      />
    </Box>
  );
}

export default AdminTable;
